import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import HelpLayout from '../../../components/help/HelpLayout';
import { 
  UserPlusIcon, 
  UserIcon, 
  AcademicCapIcon,
  CheckCircleIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';

const GettingStarted = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { name: 'User Guide', href: '/help/getting-started' },
    { name: 'Getting Started' }
  ];

  const steps = [
    {
      title: 'Create Your Account',
      description: 'Register for a free account to access all training programs',
      icon: UserPlusIcon,
      details: [
        'Visit the registration page and provide your basic information',
        'Use a valid email address as this will be your login username',
        'Choose a strong password with at least 8 characters',
        'Verify your email address by clicking the link sent to your inbox'
      ],
      action: { text: 'Register Now', href: '/register' }
    },
    {
      title: 'Complete Your Profile',
      description: 'Add your personal and educational information',
      icon: UserIcon,
      details: [
        'Fill in your personal details including ID number and contact information',
        'Add your educational background (matric results, tertiary qualifications)',
        'Provide your location details for course allocation',
        'Upload a profile picture - hover over the upload area to see file requirements',
        'Complete all sections for faster course application processing'
      ],
      action: { text: 'Edit Profile', href: '/profile' }
    },
    {
      title: 'Browse Training Programs',
      description: 'Explore available courses and find programs that match your interests',
      icon: AcademicCapIcon,
      details: [
        'Use the search and filter options to find relevant courses',
        'Read course descriptions, requirements, and duration details',
        'Check course schedules and campus locations',
        'Add interesting courses to your wishlist for later',
        'Get personalized recommendations through Career Guidance'
      ],
      action: { text: 'Browse Courses', href: '/courses' }
    },
    {
      title: 'Explore Career Opportunities',
      description: 'Discover job opportunities, apprenticeships, and internships',
      icon: CheckCircleIcon,
      details: [
        'Browse available job opportunities and apprenticeships',
        'Use Career Guidance to get personalized recommendations',
        'Save interesting opportunities to your wishlist',
        'Apply for positions that match your skills and interests'
      ],
      action: { text: 'Explore Opportunities', href: '/opportunities' }
    },
    {
      title: 'Apply for Training & Jobs',
      description: 'Submit applications for your chosen programs and opportunities',
      icon: CheckCircleIcon,
      details: [
        'Ensure your profile is complete before applying',
        'Read application requirements carefully',
        'Prepare any required documents (certificates, ID copy)',
        'Submit your applications and track status in your dashboard'
      ],
      action: { text: 'View Dashboard', href: '/dashboard' }
    }
  ];

  const quickTips = [
    {
      title: 'Complete Your Career Assessment',
      description: 'Use the Career Guidance system to get personalized course and opportunity recommendations based on your skills and interests.'
    },
    {
      title: 'Keep Your Profile Updated',
      description: 'Regularly update your contact information and qualifications to ensure you receive relevant course and job recommendations.'
    },
    {
      title: 'Check Your Dashboard Regularly',
      description: 'After logging in, you\'ll be taken directly to your dashboard which shows application status, upcoming deadlines, and important announcements.'
    },
    {
      title: 'Use the Wishlist Feature',
      description: 'Save courses and opportunities you\'re interested in to your wishlist so you can easily find them later.'
    },
    {
      title: 'Explore All Opportunities',
      description: 'Don\'t just look at courses - explore job opportunities, apprenticeships, and internships to find your ideal career path.'
    },
    {
      title: 'Enable Notifications',
      description: 'Turn on email and push notifications to stay informed about course updates, job postings, and application deadlines.'
    }
  ];

  return (
    <HelpLayout title="Getting Started" breadcrumbs={breadcrumbs}>
      <div className="help-content">
        {/* Introduction */}
        <div className="card" style={{ marginBottom: '32px' }}>
          <div className="card-body">
            <h2 style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: 'var(--primary-black)',
              marginBottom: '16px'
            }}>
              Welcome to GMSP3 Career Guidance
            </h2>
            <p style={{
              fontSize: '16px',
              color: 'var(--text-gray)',
              lineHeight: 1.6,
              marginBottom: '16px'
            }}>
              GMSP3 Career Guidance is the Gauteng Provincial Government training portal that connects
              learners with quality technical and vocational education opportunities. This guide will 
              help you get started and make the most of the platform.
            </p>
            <div style={{
              padding: '16px',
              backgroundColor: 'var(--primary-yellow-light)',
              borderRadius: '8px',
              border: '1px solid var(--primary-yellow)'
            }}>
              <p style={{
                fontSize: '14px',
                color: 'var(--primary-yellow-dark)',
                margin: 0,
                fontWeight: '500'
              }}>
                💡 <strong>Pro Tip:</strong> Complete all steps in order for the best experience. 
                A complete profile increases your chances of course acceptance.
              </p>
            </div>
          </div>
        </div>

        {/* Step-by-Step Guide */}
        <div style={{ marginBottom: '48px' }}>
          <h3 style={{
            fontSize: '20px',
            fontWeight: 'bold',
            color: 'var(--primary-black)',
            marginBottom: '24px'
          }}>
            Step-by-Step Guide
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={index} className="card">
                  <div className="card-body">
                    <div style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '16px',
                      marginBottom: '16px'
                    }}>
                      <div style={{
                        width: '48px',
                        height: '48px',
                        backgroundColor: 'var(--primary-yellow)',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <Icon style={{ width: '24px', height: '24px', color: 'var(--primary-black)' }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          marginBottom: '8px'
                        }}>
                          <span style={{
                            fontSize: '14px',
                            fontWeight: 'bold',
                            color: 'var(--primary-yellow-dark)',
                            backgroundColor: 'var(--primary-yellow-light)',
                            padding: '4px 8px',
                            borderRadius: '4px'
                          }}>
                            Step {index + 1}
                          </span>
                          <h4 style={{
                            fontSize: '18px',
                            fontWeight: 'bold',
                            color: 'var(--primary-black)',
                            margin: 0
                          }}>
                            {step.title}
                          </h4>
                        </div>
                        <p style={{
                          fontSize: '16px',
                          color: 'var(--text-gray)',
                          marginBottom: '16px'
                        }}>
                          {step.description}
                        </p>
                        <ul style={{
                          listStyle: 'none',
                          padding: 0,
                          margin: '0 0 16px 0'
                        }}>
                          {step.details.map((detail, detailIndex) => (
                            <li key={detailIndex} style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: '8px',
                              marginBottom: '8px',
                              fontSize: '14px',
                              color: 'var(--text-gray)'
                            }}>
                              <CheckCircleIcon style={{
                                width: '16px',
                                height: '16px',
                                color: 'var(--primary-yellow)',
                                marginTop: '2px',
                                flexShrink: 0
                              }} />
                              {detail}
                            </li>
                          ))}
                        </ul>
                        <Link
                          to={step.action.href}
                          className="btn btn-primary"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontSize: '14px',
                            padding: '8px 16px'
                          }}
                        >
                          {step.action.text}
                          <ArrowRightIcon style={{ width: '16px', height: '16px' }} />
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Quick Tips */}
        <div style={{ marginBottom: '32px' }}>
          <h3 style={{
            fontSize: '20px',
            fontWeight: 'bold',
            color: 'var(--primary-black)',
            marginBottom: '24px'
          }}>
            Quick Tips for Success
          </h3>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '16px'
          }}>
            {quickTips.map((tip, index) => (
              <div key={index} style={{
                padding: '16px',
                backgroundColor: 'var(--background-gray)',
                borderRadius: '8px',
                border: '1px solid var(--border-gray)'
              }}>
                <h4 style={{
                  fontSize: '16px',
                  fontWeight: 'bold',
                  color: 'var(--primary-black)',
                  marginBottom: '8px'
                }}>
                  {tip.title}
                </h4>
                <p style={{
                  fontSize: '14px',
                  color: 'var(--text-gray)',
                  margin: 0,
                  lineHeight: 1.5
                }}>
                  {tip.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Next Steps */}
        <div className="card">
          <div className="card-body">
            <h3 style={{
              fontSize: '20px',
              fontWeight: 'bold',
              color: 'var(--primary-black)',
              marginBottom: '16px'
            }}>
              What's Next?
            </h3>
            <p style={{
              fontSize: '16px',
              color: 'var(--text-gray)',
              marginBottom: '24px'
            }}>
              Now that you know the basics, explore these helpful resources:
            </p>
            <div style={{
              display: 'flex',
              gap: '16px',
              flexWrap: 'wrap'
            }}>
              <Link to="/help/courses" className="btn btn-outline">
                Course Browsing Guide
              </Link>
              <Link to="/help/applications" className="btn btn-outline">
                Application Process
              </Link>
              <Link to="/help/profile" className="btn btn-outline">
                Profile Management
              </Link>
              <Link to="/help/faq" className="btn btn-outline">
                Frequently Asked Questions
              </Link>
            </div>
          </div>
        </div>
      </div>
    </HelpLayout>
  );
};

export default GettingStarted;
